import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { CourseService, Course } from '../services/supabase';

const CourseDetailPage: React.FC = () => {
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedHoleIndex, setSelectedHoleIndex] = useState(0);

  useEffect(() => {
    const loadCourse = async () => {
      try {
        setLoading(true);
        // Get course ID from URL
        const pathParts = window.location.pathname.split('/');
        const courseId = pathParts[pathParts.length - 1];
        
        if (!courseId) {
          setError('Course not found');
          return;
        }

        console.log('Loading course:', courseId);
        const courseData = await CourseService.getCourseById(courseId);
        
        if (courseData) {
          setCourse(courseData);
        } else {
          setError('Course not found');
        }
      } catch (err) {
        setError('Failed to load course. Please try again later.');
        console.error('Error loading course:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <p className="text-gray-500 text-lg">Loading course details...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Course not found</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button 
                onClick={() => window.location.href = '/courses'} 
                className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-16">
        {/* Course Header */}
        <section className="py-12 relative bg-white text-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-6">
              <div className="mb-6">
                <button 
                  className="px-6 py-3 border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => window.location.href = '/courses'}
                >
                  ← Back to Courses
                </button>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {course.name}
              </h1>
              <p className="text-xl text-gray-900">
                {course.location}
              </p>
            </div>
          </div>
        </section>

        {/* Course Details */}
        <section className="py-8">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">

              {/* Hole Selector */}
              {course.holes && course.holes.length > 0 && (() => {
                const sortedHoles = [...course.holes].sort((a, b) => a.hole_number - b.hole_number);
                const hole = sortedHoles[selectedHoleIndex];
                return (
                  <>
                    <div className="flex flex-wrap gap-2 mb-8 justify-center">
                      {sortedHoles.map((h, index) => (
                        <button
                          key={h.hole_number}
                          onClick={() => setSelectedHoleIndex(index)}
                          className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                            index === selectedHoleIndex
                              ? 'text-white shadow-md'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          style={index === selectedHoleIndex ? { backgroundColor: '#9CB75A' } : undefined}
                        >
                          {h.hole_number}
                        </button>
                      ))}
                    </div>

                    <div className="space-y-8">
                      {/* Prize info */}
                      <div className="text-center py-4">
                        <p className="text-lg text-gray-500">Download the app to see today's prizes and sponsors for this hole</p>
                      </div>

                      {/* Hole Statistics */}
                      <div className="pt-4 border-t border-gray-200">
                        <div className="grid grid-cols-3 text-center">
                          <div>
                            <span className="text-lg font-bold text-gray-900">{hole.total_shots || 0}</span>
                            <p className="text-sm text-gray-500">Total Shots</p>
                          </div>
                          <div>
                            <span className="text-lg font-bold text-gray-900">{hole.hole_in_ones || 0}</span>
                            <p className="text-sm text-gray-500">Hole-in-Ones</p>
                          </div>
                          <div>
                            <span className="text-lg font-bold text-gray-900">{hole.shots_since_last_hio || 0}</span>
                            <p className="text-sm text-gray-500">Shots since last HIO</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}

            </div>
          </div>
        </section>
      </main>

      {/* Download CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ready to Win?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Download the app for free and start going after prizes today.
            </p>
            <a
              href="https://apps.apple.com/us/app/hole-in-1-record-golf-shots/id6740539498"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <img
                src="/branding/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                alt="Download on the App Store"
                className="h-14 mx-auto"
              />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CourseDetailPage;
